import { ArrowLeft, Briefcase, Camera, Edit, Hash, Trash2, User as UserIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import InlineEditDate from '../components/InlineEditDate';
import InlineEditSelect from '../components/InlineEditSelect';
import InlineEditText from '../components/InlineEditText';
import ScanHistoryCard from '../components/ScanHistoryCard';
import TaskActivityFeed from '../components/TaskActivityFeed';
import TaskChecklistCard from '../components/TaskChecklistCard';
import TaskCommentsSection from '../components/TaskCommentsSection';
import TaskPhotosSection from '../components/TaskPhotosSection';
import TaskStatusBadge from '../components/TaskStatusBadge';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Task, TaskActivity, TaskChecklistItem, TaskComment, TaskPriority, TaskStatus } from '../types/task';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface TaskUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

const TaskDetailsJira: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [task, setTask] = useState<Task | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<TaskUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [checklistItems, setChecklistItems] = useState<TaskChecklistItem[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [activities, setActivities] = useState<TaskActivity[]>([]);
  const [checklistProgress, setChecklistProgress] = useState({ total: 0, completed: 0, percentage: 0 });

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Get navigation context
  const returnTo = (location.state as any)?.returnTo;
  const jobId = (location.state as any)?.jobId;
  const tab = (location.state as any)?.tab || 'overview';

  const handleBack = () => {
    if (returnTo === 'job' && jobId) {
      navigate(`/jobs/${jobId}?tab=${tab}`);
    } else {
      navigate('/tasks');
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadTaskDetails();
    loadUsers();
  }, [id]);

  const loadTaskDetails = async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      const token = localStorage.getItem('token');

      const [taskRes, activityRes] = await Promise.all([
        fetch(`/api/tasks/${id}?include_checklist=true&include_comments=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/tasks/${id}/activity`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const taskData = await taskRes.json();
      const activityData = await activityRes.json();

      if (taskData.success) {
        setTask(taskData.data);
        const items = taskData.data.checklist_items || [];
        setChecklistItems(items);
        setComments(taskData.data.comments || []);

        // Calculate checklist progress
        const total = items.length;
        const completed = items.filter((item: TaskChecklistItem) => item.is_completed).length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        setChecklistProgress({ total, completed, percentage });
      }

      if (activityData.success) {
        setActivities(activityData.data);
      }
    } catch (err) {
      console.error('Error loading task:', err);
      setErrorMessage('Failed to load task details');
      setErrorDialogOpen(true);
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users?is_active=true&limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.users) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const updateField = async (field: string, value: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/fields`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ field, value }),
      });

      const data = await response.json();
      if (data.success) {
        setTask(data.data);
        // Reload activity to show the change
        loadTaskDetails(false);
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setErrorMessage(`Failed to update ${field}`);
      setErrorDialogOpen(true);
      throw error;
    }
  };

  const handleToggleChecklist = async (itemId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist/${itemId}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        loadTaskDetails(false);
      }
    } catch (error) {
      console.error('Error toggling checklist:', error);
    }
  };

  const handleAddChecklistItem = async (label: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label, item_order: checklistItems.length + 1 }),
      });
      const data = await response.json();
      if (data.success) {
        loadTaskDetails(false);
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  const handleDeleteChecklistItem = async (itemId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        loadTaskDetails(false);
      }
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  const handleAddComment = async (comment: string, mentions?: string[]) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ comment, mentions: mentions || [] }),
      });
      const data = await response.json();
      if (data.success) {
        loadTaskDetails(false);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        loadTaskDetails(false);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleDeleteTask = async () => {
    if (!task) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${task.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        if (returnTo === 'job' && jobId) {
          navigate(`/jobs/${jobId}?tab=${tab}`);
        } else {
          navigate('/tasks');
        }
      } else {
        setErrorMessage(data.message || 'Failed to delete task');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setErrorMessage('An error occurred while deleting the task');
      setErrorDialogOpen(true);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getPriorityBadge = (priority: TaskPriority) => {
    const priorityMap: Record<TaskPriority, 'default' | 'warning' | 'destructive' | 'secondary'> = {
      urgent: 'destructive',
      high: 'warning',
      normal: 'default',
      low: 'secondary',
    };
    return priorityMap[priority] || 'default';
  };

  const canEditChecklist = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key));

  const statusOptions = [
    { value: TaskStatus.PENDING, label: 'Pending' },
    { value: TaskStatus.ASSIGNED, label: 'Assigned' },
    { value: TaskStatus.IN_PROGRESS, label: 'In Progress' },
    { value: TaskStatus.COMPLETED, label: 'Completed' },
    { value: TaskStatus.BLOCKED, label: 'Blocked' },
    { value: TaskStatus.CANCELLED, label: 'Cancelled' },
  ];

  const priorityOptions = [
    { value: TaskPriority.LOW, label: 'Low' },
    { value: TaskPriority.NORMAL, label: 'Normal' },
    { value: TaskPriority.HIGH, label: 'High' },
    { value: TaskPriority.URGENT, label: 'Urgent' },
  ];

  const userOptions = [
    { value: '', label: 'Unassigned' },
    ...users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` })),
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="tasks" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading task details...</div>
        </main>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="tasks" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Task not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="tasks" />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-2xl font-bold truncate">{task.task_number}</h1>
            <span className="text-muted-foreground text-sm shrink-0">{formatTaskType(task.task_type)}</span>
          </div>
          <Button variant="outline" size="sm" className="ml-auto shrink-0" onClick={() => navigate(`/tasks/${id}/edit`, { state: { returnTo, jobId, tab } })}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Task
          </Button>
        </div>

        {/* Two-Column Layout: Main Content (70%) + Sidebar (30%) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
          {/* Main Content - Left Side */}
          <div className="space-y-6 min-w-0">
            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <InlineEditText
                  value={task.instructions ?? null}
                  onSave={(value) => updateField('instructions', value)}
                  multiline
                  placeholder="Add a description..."
                />
              </CardContent>
            </Card>

            {/* Checklist */}
            {checklistItems.length > 0 && (
              <TaskChecklistCard
                taskId={task.id}
                items={checklistItems}
                progress={checklistProgress}
                onToggle={handleToggleChecklist}
                onAdd={canEditChecklist ? handleAddChecklistItem : undefined}
                onDelete={canEditChecklist ? handleDeleteChecklistItem : undefined}
                canEdit={canEditChecklist}
              />
            )}

            {/* Photos Section */}
            <TaskPhotosSection taskId={task.id} />

            {/* Comments Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Comments</CardTitle>
              </CardHeader>
              <CardContent>
                <TaskCommentsSection
                  taskId={task.id}
                  comments={comments}
                  currentUser={user}
                  onAdd={handleAddComment}
                  onDelete={handleDeleteComment}
                />
              </CardContent>
            </Card>
            {/* Activity Feed */}
            <TaskActivityFeed activities={activities} />

            {/* Scan History */}
            {id && (
              <ScanHistoryCard
                title="Scan History"
                endpoint={`/api/scan/history/task/${id}`}
                emptyMessage="No scans recorded for this task"
              />
            )}
          </div>

          {/* Sidebar - Right Side */}
          <div className="lg:w-80 space-y-4">
            {/* Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Status
                  </label>
                  <InlineEditSelect
                    value={task.status}
                    options={statusOptions}
                    onSave={(value) => updateField('status', value)}
                    renderValue={(value) => <TaskStatusBadge status={value as TaskStatus} />}
                    className="w-full justify-start"
                  />
                </div>

                {/* Assignee */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Assignee
                  </label>
                  <InlineEditSelect
                    value={task.assigned_to_user_id || ''}
                    options={userOptions}
                    onSave={(value) => updateField('assigned_to_user_id', value || null)}
                    renderValue={(value) => {
                      const assignedUser = users.find(u => u.id === value);
                      return (
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {assignedUser ? `${assignedUser.first_name} ${assignedUser.last_name}` : 'Unassigned'}
                          </span>
                        </div>
                      );
                    }}
                    className="w-full justify-start"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Priority
                  </label>
                  <InlineEditSelect
                    value={task.priority}
                    options={priorityOptions}
                    onSave={(value) => updateField('priority', value)}
                    renderValue={(value) => (
                      <Badge variant={getPriorityBadge(value as TaskPriority)} className="text-xs">
                        {value.toUpperCase()}
                      </Badge>
                    )}
                    className="w-full justify-start"
                  />
                </div>

                {/* Task Order */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Task Order
                  </label>
                  <div className="flex items-center gap-2 py-1 px-2">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">#{task.task_order}</span>
                  </div>
                </div>

                {/* Planned Start */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Planned Start
                  </label>
                  <InlineEditDate
                    value={task.planned_start_at ?? null}
                    onSave={(value) => updateField('planned_start_at', value)}
                    placeholder="Not set"
                    className="w-full justify-start"
                  />
                </div>

                {/* Planned End */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Planned End
                  </label>
                  <InlineEditDate
                    value={task.planned_end_at ?? null}
                    onSave={(value) => updateField('planned_end_at', value)}
                    placeholder="Not set"
                    className="w-full justify-start"
                  />
                </div>

                {/* Photo Required */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Photo Required
                  </label>
                  <div className="flex items-center gap-2 py-1 px-2">
                    <Camera className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{task.requires_photo ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Related Job */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                    Related Job
                  </label>
                  {task.job ? (
                    <button
                      onClick={() => navigate(`/jobs/${task.job_id}`)}
                      className="flex items-center gap-2 py-1 px-2 hover:bg-muted rounded transition-colors w-full text-left"
                    >
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-primary underline-offset-2 hover:underline">
                        {task.job.job_number}
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 py-1 px-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">No job assigned</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Delete Button */}
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Task
            </Button>
          </div>
        </div>

        {/* Dialogs */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Task"
          description={`Are you sure you want to delete task ${task.task_number}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteTask}
          variant="destructive"
        />

        <MessageDialog
          open={errorDialogOpen}
          onOpenChange={setErrorDialogOpen}
          title="Error"
          description={errorMessage}
          variant="destructive"
        />
      </main>
    </div>
  );
};

export default TaskDetailsJira;

